import { Entity } from '@api/api/entity-schema';
import { Button } from '@design/components/button/Button';
import { Drawer } from '@design/components/drawer/Drawer';
import { DrawerContentWrapper } from '@terminal/components/drawer-content-wrapper/DrawerContentWrapper';
import { WebOnlyFeat } from '@terminal/config/WebOnlyFeat';
import { useNavigateToAssetOverview } from '@terminal/hooks/use-navigate-to-asset-overview/useNavigateToAssetOverview';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import {
  CompetitorData,
  useCompetitorsData,
} from '@terminal/widgets/knowledge-graph-widget/hooks/useCompetitorsData';
import { useTranslation } from 'react-i18next';

import { CompetitorsWidget } from '../competitors-widget/CompetitorsWidget';
import { useCompetitorsDrawer } from './useCompetitorsDrawer';

const buildFallbackCompetitorData = (
  entity: Entity,
  company: string
): CompetitorData => ({
  entity,
  competitorData: {
    company,
    relationship: {
      id: '',
      description: '',
      rank: 0,
      rank_change: null,
      evidence: null,
      source_evidence: null,
    },
    competing_themes: [],
  },
  competingThemes: [],
});

export interface CompetitorsDrawerProps {
  entity: Entity;
  fallbackCompetitorEntity?: Entity;
}

export const CompetitorsDrawer = ({
  entity,
  fallbackCompetitorEntity,
}: CompetitorsDrawerProps) => {
  const { t } = useTranslation(['widget']);
  const { navigateToPage } = useNavigateToAssetOverview();
  const { closeDrawer, isDrawerOpen, competitorTag } = useCompetitorsDrawer();

  const { selectedCompetitor, competitorsByTag } = useCompetitorsData({
    entityTag: entity.tag,
    enabled: isDrawerOpen && !!competitorTag,
  });

  let competitor: CompetitorData | undefined;
  if (selectedCompetitor) {
    competitor = competitorsByTag[selectedCompetitor];
    if (!competitor && fallbackCompetitorEntity) {
      competitor = buildFallbackCompetitorData(
        fallbackCompetitorEntity,
        selectedCompetitor
      );
    }
  }

  return (
    <Drawer
      data-testid="competitors-drawer"
      isOpen={isDrawerOpen && !!competitor}
      handleCloseClick={closeDrawer}
      headerButton={
        <WebOnlyFeat>
          <Button
            variant="empty"
            size="xsmall"
            label={t('themeOverview.drawer.assetOverview')}
            iconName="Expand"
            onClick={() =>
              navigateToPage({
                entityTag: competitorTag ?? '',
                source: POSTHOG_TRACKING_SOURCES.COMPETITORS_DRAWER,
              })
            }
          />
        </WebOnlyFeat>
      }
    >
      {competitor && (
        <DrawerContentWrapper>
          <CompetitorsWidget entity={entity} competitorData={competitor} />
        </DrawerContentWrapper>
      )}
    </Drawer>
  );
};
